import React, { useState } from 'react';
import { UserCircle, Users, ChevronDown, ExternalLink } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";


const GraduationCap = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    </svg>
);

const LinkedInButton = ({ url }) => {
    if (!url) return null;

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full text-xs font-semibold
            bg-[#0077B5]/10 text-[#0077B5] hover:bg-[#0077B5] hover:text-white transition-all duration-200 border
            border-[#0077B5]/20 hover:border-[#0077B5] group/btn"
        >
            <FontAwesomeIcon icon={faLinkedin} className="text-sm" />
            LinkedIn
            <ExternalLink className="w-3 h-3 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
        </a>
    );
};

const MemberCard = ({ member, tier = 'scientist' }) => {
    const initials = member.name
        .split(' ')
        .filter(n => n.length > 1)
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const styles = {
        scientist: {
            avatar: 'from-blue-500 to-blue-600',
            bar: 'from-blue-500 to-purple-500',
            border: 'hover:border-blue-200',
        },
        affiliated: {
            avatar: 'from-blue-500 to-blue-600',
            bar: 'from-blue-400 to-blue-500',
            border: 'hover:border-blue-200',
        },
    };

    const s = styles[tier];

    return (
        <div className={`relative bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100 ${s.border} group overflow-hidden p-5`}>
            <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${s.bar} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.avatar} flex items-center justify-center text-sm font-bold text-white mb-3 shadow`}>
                {initials}
            </div>
            <h5 className="font-semibold text-gray-900 text-sm leading-tight mb-0.5">{member.name}</h5>
            <p className="text-xs text-gray-500 leading-snug">{member.role}</p>
            {member.institution && (
                <p className="text-xs text-blue-400 font-medium mt-1">{member.institution}</p>
            )}
            <LinkedInButton url={member.linkedin} />
        </div>
    );
};

const ManagementHierarchy = () => {
    const [scientistsOpen, setScientistsOpen] = useState(true);
    const [affiliatedOpen, setAffiliatedOpen] = useState(true);

    const leadership = {
        role: 'Lead Scientist, AOSP EA Node',
        name: 'Prof Muliaro Wafula',
        linkedin: 'https://www.linkedin.com/in/joseph-wafula-768a2371/',
    };

    const coordinators = [
        {
            role: 'Coordinator, Research & Capacity Building',
            name: 'Dr Agnes Mindila',
            linkedin: 'https://www.linkedin.com/in/dr-agnes-mindila-337294a0/',
            isResearchLead: true,
        },
        {
            role: 'Coordinator, Operations',
            name: 'AICAD Officer',
            linkedin: '',
        },
        {
            role: 'Coordinator, Development, Innovation & Partnerships',
            name: 'Alex Musyoki',
            linkedin: '#',
        },
    ];

    const researchScientists = [
        { role: 'Expert, AI Model Development', name: 'Brian Macharia', linkedin: 'https://www.linkedin.com/in/brian-macharia/' },
        { role: 'Expert, Data Science and AI', name: 'Maurice Njoroge', linkedin: 'https://www.linkedin.com/in/mauricenjoroge/' },
        { role: 'Expert, Infrastructure Federation', name: 'Arthur Wainaina', linkedin: '#' },
        { role: 'Expert, Data Engineer', name: 'Kevin Miguta', linkedin: 'https://www.linkedin.com/in/kevin-miguta-ba90a960/' },
        { role: 'Expert, Systems Integration & Security', name: "Brian Ng'eno", linkedin: 'https://www.linkedin.com/in/kbrianngeno/' },
        { role: 'Expert, Smart Learning', name: 'George Mwangi', linkedin: '#' },
        { role: 'Expert, System Design & Commercialization', name: 'Joan Ajiambo', linkedin: '#' },
    ];

    const affiliatedResearchers = [
        { role: 'Global Health Leader, Data Science', name: 'Dr. Agnes Kiragga', institution: 'African Population and Health Research Centre', linkedin: 'https://www.linkedin.com/in/agnes-kiragga-6039013a/' },
        { role: 'Senior Lecturer, Computer Science', name: 'Dr. Audrey Masizana', institution: 'University of Botswana', linkedin: 'https://www.linkedin.com/in/dr-audrey-masizana-998a8910/' },
        { role: 'Professor, College of Humanities Doctoral Academy', name: 'Prof. Johannes John-Langba', institution: 'University of KwaZulu-Natal', linkedin: 'https://www.linkedin.com/in/johannes-john-langba-3500509/' },
        { role: 'Professor, Computer Science', name: 'Prof. Benjamin Aribisala', institution: 'Lagos State University', linkedin: 'https://www.linkedin.com/in/benjamin-aribisala-3902443b/' },
    ];

    const getInitials = (name) =>
        name.split(' ').filter(n => n.length > 1).map(n => n[0]).join('').slice(0, 2).toUpperCase();

    return (
        <div className="min-h-screen bg-white py-12 px-4">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
               
                .ds { font-family: 'DM Sans', system-ui, sans-serif; }
                .vline { width: 2px; background: linear-gradient(to bottom, #c7d2fe, #e0e7ff); margin: 0 auto; }
                .vline-em { width: 2px; background: linear-gradient(to bottom, #818cf8, #c7d2fe); margin: 0 auto; }
                .vline-green { width: 2px; background: linear-gradient(to bottom, #6ee7b7, #d1fae5); margin: 0 auto; }
            `}</style>

            <div className="max-w-5xl mx-auto ds">

                {/* Page title */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 mb-12 shadow-2xl">
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-3 flex items-center gap-3">
                        <UserCircle className="w-10 h-10"/>
                        Management Team
                    </h3>
                    <p className="text-blue-100 text-lg">Operational leadership and technical expertise driving
                        innovation</p>
                </div>

                {/* ── LEVEL 1 : Leadership ── */}
                <div className="flex flex-col items-center">

                    <div className="w-full max-w-xs">
                        <div
                            className="relative bg-white rounded-2xl shadow-md border-2 border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden p-6 text-center group">
                            <div
                                className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-600 to-slate-400" />
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-500 flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4 shadow-lg">
                                {getInitials(leadership.name)}
                            </div>
                            <span className="inline-block bg-slate-100 text-slate-500 text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-2">Lead Scientist</span>
                            <h3 className="ph text-xl font-bold text-gray-900">{leadership.name}</h3>
                            <p className="text-xs text-gray-400 mt-1">{leadership.role}</p>
                            <div className="flex justify-center">
                                <LinkedInButton url={leadership.linkedin} />
                            </div>
                        </div>
                    </div>

                    {/* connector */}
                    <div className="vline h-8" />

                    {/* ── LEVEL 2 : Coordinators ── */}
                    <p className="text-xs tracking-[0.25em] text-gray-400 uppercase font-medium mb-5">Coordinators</p>

                    <div className="w-full max-w-4xl">
                        {/* Horizontal top bar connecting all 3 */}
                        <div className="hidden md:flex justify-between px-[16.67%] mb-0 relative">
                            <div className="absolute top-0 left-[16.67%] right-[16.67%] h-px bg-gray-200" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {coordinators.map((coord, idx) => (
                                <div key={idx} className="relative flex flex-col items-center">
                                    {/* drop line from horizontal bar */}
                                    <div className="hidden md:block vline h-5 mb-0" />

                                    <div className={`w-full relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border-2 group ${coord.isResearchLead ? 'border-blue-200 hover:border-blue-400' : 'border-gray-100 hover:border-gray-200'}`}>
                                        <div className={`absolute top-0 left-0 right-0 h-1 ${coord.isResearchLead ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-gradient-to-r from-sky-400 to-blue-400 opacity-0 group-hover:opacity-100'} transition-opacity duration-300`} />
                                        <div className="p-5">
                                            {coord.isResearchLead && (
                                                <span className="inline-block bg-blue-50 text-blue-500 text-xs font-semibold px-2 py-0.5 rounded-md mb-2 tracking-wide">Research Lead</span>
                                            )}
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white mb-3 shadow ${coord.isResearchLead ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-sky-400 to-blue-500'}`}>
                                                {getInitials(coord.name)}
                                            </div>
                                            <h5 className="font-semibold text-gray-900 text-sm leading-tight mb-0.5">{coord.name}</h5>
                                            <p className="text-xs text-gray-500 leading-snug">{coord.role}</p>
                                            <LinkedInButton url={coord.linkedin} />
                                        </div>
                                    </div>

                                    {/* Down arrow from Agnes only */}
                                    {coord.isResearchLead && (
                                        <div className="vline-em h-8 mt-0" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* "Reports to Agnes" label */}
                    <div className="flex items-center gap-2 mt-1 mb-1">
                        <div className="h-px w-10 bg-blue-200" />
                        <span className="text-xs text-blue-400 font-medium tracking-wide">Reports to Dr. Agnes Mindila</span>
                        <div className="h-px w-10 bg-blue-200" />
                    </div>
                    <div className="vline-em h-6" />

                    {/* ── LEVEL 3 : Research Scientists (toggle) ── */}
                    <button
                        onClick={() => setScientistsOpen(!scientistsOpen)}
                        className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none"
                    >
                        <Users className="w-4 h-4" />
                        Research Scientists
                        <span className="bg-blue-200 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold">{researchScientists.length}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${scientistsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div className="vline h-4" />

                    <div className={`w-full max-w-4xl transition-all duration-500 overflow-hidden ${scientistsOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {researchScientists.map((m, i) => (
                                <MemberCard key={i} member={m} tier="scientist" />
                            ))}
                        </div>
                    </div>

                    {/* Separator before affiliated */}
                    <div className="w-full max-w-4xl mt-10">
                        <div className="flex items-center gap-4">
                            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-blue-200" />
                            <span className="text-xs tracking-[0.25em] text-blue-500 uppercase font-medium">Affiliated Researchers</span>
                            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-blue-200" />
                        </div>
                    </div>
                    <div className="vline-green h-6" />

                    {/* ── LEVEL 4 : Affiliated Researchers (toggle) ── */}
                    <button
                        onClick={() => setAffiliatedOpen(!affiliatedOpen)}
                        className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none"
                    >
                        <GraduationCap className="w-4 h-4" />
                        Affiliated Researchers
                        <span className="bg-blue-200 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold">{affiliatedResearchers.length}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${affiliatedOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div className="vline-green h-4" />

                    <div className={`w-full max-w-4xl transition-all duration-500 overflow-hidden ${affiliatedOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {affiliatedResearchers.map((m, i) => (
                                <MemberCard key={i} member={m} tier="affiliated" />
                            ))}
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
};

export default ManagementHierarchy;